// System-UI hotkeys — the old main.ts keydown god-listener reborn as a
// per-frame function over the action table. Runs BEFORE the freeze gates so
// panels stay closable while the sim is frozen.
//
// Deliberate change from the listener era (pinned in tests): an open panel
// puts actions in 'menu' context, so gameplay hotkeys (C, I, /) no longer
// leak through the pickup card or gallery.
//
// Ownership (Phase E, amoeba-input plan): the pickup card's own confirm/
// cancel moved to focus-nav.ts (direction nav + a real .click() on the
// focused Wear/Pocket button) — this module no longer touches it, so a
// menuConfirm/menuCancel press can't fire twice in one frame. What stays
// here: shop cancel (menuCancel/raw C) and stats-card cancel, plus all
// gameplay-context toggles.

import { motherPosition } from '../game/systems/npc'
import { T } from '../game/tunables'
import { settings } from '../game/settings'
import { showNotice } from './skill-notice'
import type { GameActions } from '../game/controls'
import type { GameWorld } from '../game/world'
import type { Entity } from '../game/components'

export interface UiHotkeysDeps {
  world: GameWorld
  playerRef: { current: Entity }
  shop: { isOpen(): boolean; open(): void; close(): void; refresh(p: Entity): void }
  statsCard: { isOpen(): boolean; toggle(p: Entity): void; close(): void }
  legend: { toggle(): void }
  orbBeacon: { pingHome(): void }
}

export function createUiHotkeys(deps: UiHotkeysDeps): { tick(actions: GameActions): void } {
  const { world, playerRef, shop, statsCard, legend, orbBeacon } = deps
  return {
    tick(actions) {
      if (shop.isOpen()) {
        // C toggles the pantry closed too — raw read, because the open shop
        // holds 'menu' context where the gameplay 'interact' is suppressed.
        if (actions.justPressed('menuCancel') || actions.pressedRaw('interact')) shop.close()
        return
      }
      if (statsCard.isOpen()) {
        if (actions.justPressed('menuCancel') || actions.pressedRaw('stats')) statsCard.close()
        return
      }

      // Gameplay context — all of these no-op while any panel holds 'menu'.
      if (actions.justPressed('interact')) {
        // The shop is her pantry, not a menu — C only works beside her.
        // From afar it points you home instead.
        const mom = motherPosition(world)
        const p = playerRef.current
        const near = mom
          ? Math.hypot(mom.x - p.position.x, mom.y - p.position.y) < T.mother.shopReach
          : false
        if (near) {
          shop.refresh(p)
          shop.open()
        } else {
          orbBeacon.pingHome()
        }
      }
      // Specimen report (A4) — read-only, so it opens anywhere, unlike the shop.
      if (actions.justPressed('stats')) statsCard.toggle(playerRef.current)
      if (actions.justPressed('legend')) legend.toggle()
      // Predation reflex: attack.ts fires on its own while a target is locked.
      if (actions.justPressed('autoFire')) {
        settings.autoFire = !settings.autoFire
        showNotice(settings.autoFire
          ? 'predation reflex engaged — the cell hunts on its own'
          : 'predation reflex suppressed')
      }
    },
  }
}
